import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "@/lib/utils";

type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  viewportRef?: React.Ref<HTMLDivElement>;
  viewportClassName?: string;
  scrollbarForceMount?: boolean;
};

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, viewportRef: viewportRefProp, viewportClassName, scrollbarForceMount = false, ...props }, ref) => {
  const internalViewportRef = React.useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = React.useState(false);

  React.useEffect(() => {
    const el = internalViewportRef.current;
    if (!el) return;
    const update = () => {
      const can = el.scrollHeight > el.clientHeight;
      setCanScroll(can);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      el.removeEventListener("scroll", update);
      if (ro) {
        ro.disconnect();
      }
    };
  }, []);

  return (
    <ScrollAreaPrimitive.Root ref={ref} className={cn("relative overflow-hidden", className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        ref={(node) => {
          internalViewportRef.current = node as HTMLDivElement | null;
          if (viewportRefProp) {
            if (typeof viewportRefProp === "function") {
              viewportRefProp(node as HTMLDivElement | null);
            } else if (typeof viewportRefProp === "object") {
              (viewportRefProp as React.MutableRefObject<HTMLDivElement | null>).current = node as HTMLDivElement | null;
            }
          }
        }}
        className={cn("h-full w-full rounded-[inherit]", viewportClassName)}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      
      <ScrollBar forceMount={scrollbarForceMount} />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> & { forceMount?: boolean }
>(({ className, orientation = "vertical", forceMount, ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    forceMount={forceMount}
    className={cn(
      "flex touch-none select-none transition-colors data-[state=hidden]:hidden",
      orientation === "vertical" && "h-full w-2.5",
      orientation === "horizontal" && "h-2.5 flex-col",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea,  };
